import {
  Button,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  Icon,
  Label,
  Text,
  Title,
} from "@assetwatch/ui";

export type AttachmentResult = {
  id: string;
  name: string;
  status: "uploaded" | "failed";
};

type RequestSubmittedViewProps = {
  attachmentResults: AttachmentResult[];
  requestNumber: string;
  onViewRequests: () => void;
  onSubmitAnother: () => void;
};

const NEXT_STEPS = [
  "The team will review your request and approve or deny it.",
  "The work will be scheduled according to priority.",
  "You get an update when the work is complete.",
];

// Identity-stable callback ref: React re-runs inline ref callbacks on every
// commit, which would steal focus back to the heading on each re-render.
function focusOnMount(node: HTMLElement | null) {
  node?.focus();
}

function SuccessMark() {
  return (
    /* @ds-deviation: the POC's success mark layers two primary tints made with color-mix (6% and 16%) around the primary disc; the DS has one primary container tint, so a single ring is used — add success-mark ring tints to the design system or accept the single ring */
    <span className="flex size-(--spacing-80) shrink-0 items-center justify-center rounded-full bg-primary-primary-container">
      <span className="flex size-(--spacing-64) items-center justify-center rounded-full bg-primary text-primary-on-primary">
        <Icon name="check-mark" size="lg" tone="inherit" />
      </span>
    </span>
  );
}

function AttachmentResults({ results }: { results: AttachmentResult[] }) {
  return (
    <ul
      aria-label="Attachments"
      className="m-0 flex w-full list-none flex-col gap-(--spacing-4) p-0"
    >
      {results.map((result) => (
        <li className="flex min-w-0 flex-col" key={result.id}>
          <span className="flex min-w-0 items-center gap-(--spacing-8) text-text-subtle">
            <Icon
              name={result.status === "uploaded" ? "check-mark" : "x-close"}
              size="xs"
              tone="inherit"
            />
            <Text as="span" truncate>
              {result.name}
            </Text>
          </span>
          {result.status === "failed" ? (
            <Text size="sm" tone="negative">
              Could not attach. The request was still created.
            </Text>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

/** Confirmation shown after a new request is created. */
export function RequestSubmittedView({
  attachmentResults,
  requestNumber,
  onViewRequests,
  onSubmitAnother,
}: RequestSubmittedViewProps) {
  return (
    <>
      <DrawerHeader closeLabel="Close New Request" title="New Request" />
      <DrawerBody gap="none">
        <div className="flex min-h-full flex-col items-center justify-center gap-(--spacing-16) bg-surface-container-low px-(--spacing-24) py-(--spacing-32)">
          <SuccessMark />
          <div className="outline-none" ref={focusOnMount} tabIndex={-1}>
            <Title align="center" as="h2" size="xl" weight="bold">
              {requestNumber} submitted
            </Title>
          </div>
          {attachmentResults.length > 0 ? (
            <AttachmentResults results={attachmentResults} />
          ) : null}
          <div className="mt-(--spacing-8) flex w-full flex-col gap-(--spacing-12) rounded-(--radius-lg) bg-surface p-(--spacing-16) shadow-elevation-1">
            <Label size="sm" tone="subtle">
              WHAT HAPPENS NEXT
            </Label>
            <ol className="m-0 list-none p-0">
              {NEXT_STEPS.map((step, index) => (
                <li
                  className="flex items-center gap-(--spacing-12) border-b border-outline-subtle py-(--spacing-8) last:border-b-0"
                  key={step}
                >
                  <span className="flex size-(--spacing-24) shrink-0 items-center justify-center rounded-full bg-surface-container-high font-(family-name:--font-family-label) text-(length:--font-size-label-sm) font-(number:--font-weight-semibold) text-text-default tabular-nums">
                    {index + 1}
                  </span>
                  <Text tone="subtle">{step}</Text>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </DrawerBody>
      <DrawerFooter>
        <div className="flex w-full flex-col gap-(--spacing-8)">
          <Button fullWidth icon="arrow-right" onClick={onViewRequests}>
            View my requests
          </Button>
          <Button
            fullWidth
            icon="edit-pen"
            onClick={onSubmitAnother}
            variant="outline"
          >
            Submit another request
          </Button>
        </div>
      </DrawerFooter>
    </>
  );
}
